import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined"
import { Box, Stack } from "@mui/material"
import { alpha } from "@mui/material/styles"

import type { LinkType } from "../../graphql/types"
import type { FC, ResponsiveImageType, iconKeys } from ".."
import { Link, ResponsiveImage, SvgIcon, WYSIWYG } from ".."

export type TeaserCardProps = {
  title: string
  description: string
  icon: string
  image: ResponsiveImageType
  link: LinkType
}

const Teaser: FC<TeaserCardProps> = ({ title, description, icon, image, link }) => {
  return (
    <Stack
      sx={(theme) => ({
        "border": `1px solid ${theme.palette["neutral.200"].main}`,
        "borderRadius": 1.5,
        "transition": theme.transitions.create(["border-color", "box-shadow"]),

        "&:hover": {
          "borderColor": "transparent",
          "boxShadow": `1px 1px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.15)}`,

          "img": {
            transform: "scale(1.1)",
          },

          ".arrow-icon": {
            opacity: 1,
            display: { xs: "none", md: "flex" },
            transform: "translateY(0)",
          },
        },
      })}
    >
      <Stack rowGap={{ xs: 5, md: 10 }} p={{ xs: 5, md: 10 }}>
        <Stack rowGap={5}>
          <SvgIcon variant={icon as iconKeys} size={{ xs: 14, md: 20 }} sx={{ opacity: 0.3 }} />
          <Link
            type="base"
            variant="heading.md"
            href={link.uri}
            aria-label={`${link.title} - ${title}`}
            sx={(theme) => ({ "&:hover": { color: theme.palette["neutral.800"].main } })}
          >
            {title}
          </Link>
          <WYSIWYG
            variant="body.sm"
            content={description}
            sx={{
              opacity: 0.7,
              overflow: "hidden",
              display: { xs: "none", md: "-webkit-box" },
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
          />
        </Stack>
        <Box position="relative">
          <Box overflow="hidden" borderRadius={1.5} sx={{ aspectRatio: "265/160" }}>
            {image ? (
              <ResponsiveImage
                image={image}
                sx={{
                  transition: (theme) =>
                    theme.transitions.create("transform", {
                      duration: theme.transitions.duration.standard * 1.5,
                    }),
                }}
              />
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                bgcolor={(theme) => theme.palette["secondary.200"].main}
                borderRadius={1.5}
                sx={{ width: "auto", height: "100%" }}
              >
                <ImageNotSupportedOutlinedIcon />
              </Stack>
            )}
          </Box>
          <Stack
            className="arrow-icon"
            alignItems="center"
            justifyContent="center"
            position="absolute"
            bottom={26}
            right={-10}
            sx={(theme) => ({
              bgcolor: theme.palette["primary.800"].main,
              borderRadius: 9999,
              color: theme.palette["neutral.100"].main,
              height: 62,
              opacity: 0,
              transition: theme.transitions.create(["opacity", "transform"]),
              transform: "translateY(26px)",
              width: 62,
            })}
          >
            <SvgIcon variant="arrowOutward" size={16} />
          </Stack>
          <Link type="overlay" href={link.uri} aria-label={`${link.title} - ${title}`} />
        </Box>
      </Stack>

      <Stack
        alignItems="center"
        justifyContent="center"
        mt="auto"
        py={4.5}
        minHeight={{ xs: 48, md: 62 }}
        borderTop={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
      >
        <Link
          variant="link.xs"
          href={link.uri}
          aria-label={`${link.title} - ${title}`}
          sx={{
            "backgroundSize": "0 1px",
            "pt": 0.75,
            "&:hover": {
              backgroundSize: "100% 1px",
            },
          }}
        >
          {link.title}
        </Link>
      </Stack>
    </Stack>
  )
}
export default Teaser
